import { useEffect } from "react";
import { Link } from "react-router-dom";
import { usePageMeta } from "@/components/pepmuse/usePageMeta";
import { Check, Palette, Tag, PanelsTopLeft, ShieldCheck, FlaskConical, Truck } from "lucide-react";
import { SubpageHero } from "@/components/pepmuse/SubpageHero";
import { PricingPanel } from "@/components/pepmuse/PricingPanel";
import { HeroArtwork } from "@/components/pepmuse/HeroArtwork";
import { ClosingCTA } from "@/components/pepmuse/ClosingCTA";
import { Reveal, Eyebrow } from "@/components/pepmuse/Reveal";

const INCLUDED = [
  { icon: FlaskConical, title: "Wholesale peptide supply", body: "A catalog of 100+ research peptides at wholesale pricing — sourced from verified suppliers with third-party COA documentation, ready to carry your label." },
  { icon: Tag, title: "White-label vials & packaging", body: "Your name, your colors, your logo on every vial and box — produced, labeled, and packed for your brand." },
  { icon: Palette, title: "Logo & brand identity", body: "A distinctive logo and visual identity shaped around your name, your market, and your taste — delivered in every format you'll need." },
  { icon: PanelsTopLeft, title: "Custom storefront", body: "A mobile-first online store built around your brand — catalog, checkout, and content, ready to take orders." },
  { icon: Truck, title: "3PL fulfillment", body: "Your inventory is stored, picked, packed, and shipped through fulfillment partners — you never touch a box." },
  { icon: ShieldCheck, title: "Compliance framework", body: "Documentation templates, research-use protocols, and practical guidance so the fine print is handled from day one. Operational guidance, not legal advice." },
];

const WHY = [
  { title: "Four to six weeks", body: "Supply, brand, storefront, and fulfillment move together on one scheduled timeline — kickoff to launch in about a month and a half." },
  { title: "Your inventory, their shelves", body: "You buy stock at wholesale when you're ready — the first-order minimum is set with the supplier — and you own it. 3PL partners store, pick, pack, and ship; storage and fulfillment fees are billed as you sell." },
  { title: "Credibility on day one", body: "Considered branding and clean documentation make a brand-new business look established." },
];

const WHO = [
  { title: "First-time founders", body: "You're entering the research peptide space and want the whole setup handled properly." },
  { title: "Research retailers", body: "You serve the research community and want a professional line with research-use positioning handled properly." },
  { title: "Industry operators", body: "You know the space — you want compliant infrastructure without rebuilding it yourself." },
];

const COSTS_INCLUDED = [
  { label: "Wholesale supply access", detail: "Supplier introduction and catalog setup at wholesale pricing, with third-party COA documentation." },
  { label: "White-label vials & packaging", detail: "Vial, label, and packaging design and production coordination for your brand. Production costs depend on your run size." },
  { label: "Logo & brand identity", detail: "Full visual identity delivered in every format — print, web, labels." },
  { label: "Custom storefront", detail: "Mobile-first store with catalog and checkout, structured for research-use marketing." },
  { label: "3PL fulfillment setup", detail: "Partner selection, integration, and launch coordination." },
  { label: "Compliance framework", detail: "Research-use documentation templates and protocols. Operational guidance, not legal advice." },
  { label: "Year-one hosting", detail: "Your store's hosting arranged and covered for the first year." },
  { label: "3 months technical support", detail: "Email support as your business begins to launch." },
];

const COSTS_ADDITIONAL = [
  { label: "Initial inventory", detail: "Purchased at wholesale after setup; the first-order minimum is set with the supplier. You own your stock." },
  { label: "3PL storage & fulfillment fees", detail: "Storage and pick/pack are billed by the fulfillment partner as you sell." },
  { label: "Per-order shipping", detail: "Carrier costs on each order, paid by you or your buyers at checkout." },
  { label: "Hosting renewal", detail: "After year one, hosting renews at the standard rate — about $297/year." },
  { label: "Extended support", detail: "An optional ongoing support plan after the included three months." },
];

export default function BusinessInABox({ onBuild }) {
  usePageMeta(
    "Business in a Box — PepMuse",
    "The complete research peptide brand package: wholesale peptide supply, white-label vials and packaging, brand identity, custom storefront, 3PL fulfillment setup, and compliance documents — one team, one timeline."
  );

  return (
    <>
      <SubpageHero
        testId="biab-hero"
        eyebrow="Business in a box · The complete start"
        title="One box. Your whole business."
        body="Wholesale supply, white-label vials, brand identity, storefront, fulfillment, and launch support — designed, built, and bundled into a single start."
        onBuild={onBuild}
        secondary={{ label: "See what's inside", target: "box-included" }}
        art={<HeroArtwork />}
      />

      <section id="box-included" data-testid="biab-included" className="scroll-mt-24 py-20 sm:py-24" tabIndex={-1}>
        <div className="mx-auto w-full max-w-shell px-5 sm:px-8">
          <Reveal>
            <Eyebrow index="01" testId="biab-included-eyebrow">What's in the box</Eyebrow>
            <h2 className="mt-4 max-w-xl font-display text-3xl font-extrabold tracking-tight text-paper sm:text-4xl">
              Everything a launch needs.
            </h2>
            <p className="mt-4 max-w-md text-base text-paper/60">
              Six pieces, one package — each one designed to connect with the others.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {INCLUDED.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.06}>
                <div data-testid={`biab-included-card-${i + 1}`} className="h-full rounded-3xl border border-white/[0.08] bg-card p-7">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-lilac-bright to-lilac-deep text-ink shadow-[0_10px_20px_-8px_rgba(154,131,216,0.6)]">
                    <item.icon className="h-5 w-5" strokeWidth={2.4} aria-hidden="true" />
                  </span>
                  <h3 className="mt-5 font-display text-lg font-extrabold text-paper">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-paper/60">{item.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="biab-pricing-section" className="py-8 sm:py-12">
        <div className="mx-auto w-full max-w-shell px-5 sm:px-8">
          <PricingPanel
            testId="biab-pricing"
            packageKey="business-in-a-box"
            eyebrow="Special launch pricing"
            title="Business in a Box"
            price="$9,997"
            regular="$12,500"
            note="One-time setup. No recurring fees from PepMuse."
            bullets={[
              "Wholesale supply & white-label vials included",
              "Brand, storefront, and 3PL fulfillment setup together",
              "Live in about four to six weeks",
            ]}
            onBuild={onBuild}
          />
        </div>
      </section>

      <section data-testid="biab-costs" className="py-20 sm:py-24">
        <div className="mx-auto w-full max-w-shell px-5 sm:px-8">
          <Reveal>
            <Eyebrow index="02" testId="biab-costs-eyebrow">Included vs. additional</Eyebrow>
            <h2 className="mt-4 max-w-xl font-display text-3xl font-extrabold tracking-tight text-paper sm:text-4xl">
              What the $9,997 covers — and what it doesn't.
            </h2>
            <p className="mt-4 max-w-md text-base text-paper/60">
              No hidden lines. Inventory and usage-based costs stay separate, so the package price stays the
              package price.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            <Reveal>
              <div data-testid="biab-costs-included" className="h-full rounded-3xl border border-white/[0.08] bg-card p-7 sm:p-8">
                <h3 className="font-display text-lg font-extrabold text-paper">Included in the package</h3>
                <ul className="mt-5 space-y-4">
                  {COSTS_INCLUDED.map((row) => (
                    <li key={row.label} className="flex gap-3">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-lilac/20 text-lilac">
                        <Check className="h-3 w-3" strokeWidth={3} aria-hidden="true" />
                      </span>
                      <div>
                        <p className="text-sm font-bold text-paper">{row.label}</p>
                        <p className="mt-0.5 text-sm leading-relaxed text-paper/55">{row.detail}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <div data-testid="biab-costs-additional" className="h-full rounded-3xl border border-lilac/25 bg-gradient-to-b from-lilac/[0.08] to-card p-7 sm:p-8">
                <h3 className="font-display text-lg font-extrabold text-paper">Additional costs, billed separately</h3>
                <ul className="mt-5 space-y-4">
                  {COSTS_ADDITIONAL.map((row) => (
                    <li key={row.label} className="flex gap-3">
                      <span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-lilac" aria-hidden="true" />
                      <div>
                        <p className="text-sm font-bold text-paper">{row.label}</p>
                        <p className="mt-0.5 text-sm leading-relaxed text-paper/55">{row.detail}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.12}>
            <p data-testid="biab-costs-ratecard-note" className="mt-8 max-w-2xl text-sm leading-relaxed text-paper/50">
              Priced individually on our standard rate card, this scope totals $24,022 — the same line items you
              can itemize on the{" "}
              <Link
                to="/checklist"
                data-testid="biab-costs-checklist-link"
                className="font-bold text-lilac underline decoration-lilac/40 underline-offset-4 transition-colors hover:text-lilac-bright"
              >
                Launch Checklist
              </Link>
              . The package bundles them at $9,997.
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="biab-why" className="py-20 sm:py-24">
        <div className="mx-auto w-full max-w-shell px-5 sm:px-8">
          <Reveal>
            <Eyebrow index="03" testId="biab-why-eyebrow">Why this route</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-paper sm:text-4xl">
              The shortest path to open.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-3">
            {WHY.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.08}>
                <div data-testid={`biab-why-card-${i + 1}`} className="h-full rounded-3xl border border-white/[0.08] bg-card p-7">
                  <span className="font-display text-lg font-extrabold text-lilac/80">0{i + 1}</span>
                  <h3 className="mt-3 font-display text-lg font-extrabold text-paper">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-paper/60">{item.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="biab-who" className="pb-20 sm:pb-24">
        <div className="mx-auto w-full max-w-shell px-5 sm:px-8">
          <Reveal>
            <Eyebrow index="04" testId="biab-who-eyebrow">Who it's for</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-paper sm:text-4xl">
              Made for three kinds of founders.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-3">
            {WHO.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.08}>
                <div data-testid={`biab-who-card-${i + 1}`} className="h-full rounded-3xl border border-white/[0.08] bg-card p-7">
                  <h3 className="font-display text-lg font-extrabold text-paper">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-paper/60">{item.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <ClosingCTA onBuild={() => onBuild()} />
    </>
  );
}
